import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'

import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Tooltip from '@mui/material/Tooltip'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import WarningIcon from '@mui/icons-material/Warning'

import Alert from '../alert/alert'
import { User } from '../../types/user'
import { useContextHook } from '../../store/context'
import { userLoaded } from '../../store/actions'
import { patch } from '../../utils/service'
import { Customer } from '../../types/customer'
import { convertFromTempScale, convertToTempScale, getValueSuffix } from '../../utils/graphUtils'
import NumberField from '../fields/numberField'
import { SeriesType } from '../../types/enums/seriesType'

interface UserSettingsProps {
  user: User
  onChangePassword: () => void
}

enum TempType {
  Celsius = 'c',
  Fahrenheit = 'f',
}

enum WeightType {
  Kilogram = 'kg',
  Pounds = 'lbs',
}

enum Setting {
  Temperature = 'temperature',
  Weight = 'weight',
  Darkmode = 'darkmode',
  Email = 'email',
  S100ChartTemp = 's100_chart_temp',
  P2000ChartTemp = 'p2000_chart_temp',
  RorInterval = 'ror_interval',
}

// in Celsius
const s100YAxisDefaultTemp = 325
const s100minYAxisTemp = 250
const s100maxYAxisTemp = 600

const UserSettings = (props: UserSettingsProps): JSX.Element => {
  const { t } = useTranslation()
  const { state, dispatch } = useContextHook()

  const [isFahrenheit, setIsFahrenheit] = React.useState<boolean>(props.user.fahrenheit)
  const [isPounds, setIsPounds] = React.useState<boolean>(props.user.is_pounds)
  const [isDarkmode, setIsDarkmode] = React.useState<boolean>(props.user.is_darkmode)
  const [s100maxTemp, sets100maxTemp] = React.useState<number>(
    props.user.s100_chart_max_temp ? convertToTempScale(props.user.s100_chart_max_temp, 0) : s100YAxisDefaultTemp
  )
  const [rorInterval, setrorInterval] = React.useState<number>(props.user.ror_interval || 30)
  const [email, setEmail] = React.useState<string>(props.user.email || '')
  const [hasEmailError, setHasEmailError] = React.useState<boolean>(false)

  const [isSavingSettings, setIsSavingSettings] = React.useState<Array<Setting>>([])
  const [hasSaveSuccesses, setHasSaveSuccesses] = React.useState<Array<Setting>>([])
  const [hasSaveErrors, setHasSaveErrors] = React.useState<Array<Setting>>([])

  const systemForcesDarkMode = useMediaQuery(
    {
      query: '(prefers-color-scheme: dark)',
    },
    undefined,
    (systemDarkMode: boolean) => {
      setForcedDarkMode(systemDarkMode)
    }
  )
  const [forcedDarkMode, setForcedDarkMode] = React.useState<boolean>(systemForcesDarkMode)

  const addSetting = (oldArray: Array<Setting>, setting: Setting): Array<Setting> => {
    return [...oldArray, setting]
  }

  const removeSetting = (oldArray: Array<Setting>, setting: Setting): Array<Setting> => {
    return oldArray.filter((x) => x !== setting)
  }

  const hasSetting = (array: Array<Setting>, setting: Setting): boolean => {
    return array.indexOf(setting) > -1
  }

  const saveSetting = <T,>(
    setting: Setting,
    promise: Promise<{ data: T }>,
    saveCallback: (savedData: T) => void,
    errorCallback: () => void
  ): void => {
    setIsSavingSettings((oldSettings) => addSetting(oldSettings, setting))
    setHasSaveSuccesses((oldSettings) => removeSetting(oldSettings, setting))

    // PATCH new value for fahrenheit
    promise
      .then((saveTempDisplayResponse: { data: T }): void => {
        setIsSavingSettings((oldSettings) => removeSetting(oldSettings, setting))
        setHasSaveSuccesses((oldSettings) => addSetting(oldSettings, setting))
        saveCallback(saveTempDisplayResponse.data)
      })
      .catch((): void => {
        setIsSavingSettings((oldSettings) => removeSetting(oldSettings, setting))
        setHasSaveErrors((oldSettings) => addSetting(oldSettings, setting))
        errorCallback()
      })
  }

  const saveDarkmodeSetting = (isDarkmodeEnabled: boolean) => {
    const savePromise = patch<{ is_darkmode: boolean }, User>(state.user?.url || '', {
      is_darkmode: isDarkmodeEnabled,
    })
    const saveCallback = () => window.location.reload()
    const errorCallback = () => setIsDarkmode(!isDarkmodeEnabled)

    saveSetting<User>(Setting.Darkmode, savePromise, saveCallback, errorCallback)
  }

  const saveTempDisplaySetting = (isFahrenheitEnabled: boolean): void => {
    const savePromise = patch<{ fahrenheit: boolean }, Customer>(state.currentCustomer?.url || '', {
      fahrenheit: isFahrenheitEnabled,
    })
    const saveCallback = (response: Customer): void => {
      dispatch(userLoaded({ ...props.user, fahrenheit: response.fahrenheit }))
      sets100maxTemp(convertToTempScale(props.user.s100_chart_max_temp || s100YAxisDefaultTemp, 0))
    }
    const errorCallback = () => setIsFahrenheit(!isFahrenheitEnabled)

    saveSetting<Customer>(Setting.Temperature, savePromise, saveCallback, errorCallback)
  }

  const saverorInterval = (rorInterval: number) => {
    const savePromise = patch<{ ror_interval: number }, User>(state.user?.url || '', {
      ror_interval: rorInterval,
    })
    const saveCallback = (response: User) =>
      dispatch(userLoaded({ ...props.user, ror_interval: response.ror_interval }))
    const errorCallback = () => setrorInterval(props.user.ror_interval)

    saveSetting<User>(Setting.RorInterval, savePromise, saveCallback, errorCallback)
  }

  const saveChartMaxTemp = (newMaxTemp: number): void => {
    const savePromise = patch<{ s100_chart_max_temp: number }, User>(state.user?.url || '', {
      s100_chart_max_temp: convertFromTempScale(newMaxTemp),
    })
    const saveCallback = (response: User) =>
      dispatch(userLoaded({ ...props.user, s100_chart_max_temp: response.s100_chart_max_temp }))
    const errorCallback = () => sets100maxTemp(convertToTempScale(props.user.s100_chart_max_temp, 0))

    saveSetting<User>(Setting.S100ChartTemp, savePromise, saveCallback, errorCallback)
  }

  const saveWeightDisplaySetting = (isPoundsEnabled: boolean): void => {
    const savePromise = patch<{ is_pounds: boolean }, User>(state.user?.url || '', {
      is_pounds: isPoundsEnabled,
    })
    const saveCallback = (response: User) => dispatch(userLoaded({ ...props.user, is_pounds: response.is_pounds }))
    const errorCallback = () => setIsPounds(!isPoundsEnabled)

    saveSetting<User>(Setting.Weight, savePromise, saveCallback, errorCallback)
  }

  const saveEmail = (newEmail: string): void => {
    const savePromise = patch<{ email: string }, User>(state.user?.url || '', {
      email: newEmail,
    })
    const saveCallback = (response: User) => dispatch(userLoaded({ ...props.user, email: response.email }))
    const errorCallback = () => setEmail(props.user.email)

    saveSetting<User>(Setting.Email, savePromise, saveCallback, errorCallback)
  }

  const isValidEmail = (email: string): boolean => {
    // taken from https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
      email
    )
  }

  return (
    <Stack alignItems="flex-start" spacing={5}>
      <Typography component="h1" variant="h4">
        {t('settingsview.globalsettings')}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={3}>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t('settingsview.weightdisplay')}</FormLabel>
          <RadioGroup
            aria-label={t('settingsview.weightdisplay')}
            defaultValue={WeightType.Kilogram}
            name="weight-display-buttons-group"
            value={isPounds ? WeightType.Pounds : WeightType.Kilogram}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              setIsPounds(event.target.value === WeightType.Pounds)
              saveWeightDisplaySetting(event.target.value === WeightType.Pounds)
            }}
          >
            <FormControlLabel value={WeightType.Kilogram} control={<Radio />} label={t('settingsview.gram')} />
            <FormControlLabel value={WeightType.Pounds} control={<Radio />} label={t('settingsview.ounce')} />
          </RadioGroup>
        </FormControl>
        {hasSetting(isSavingSettings, Setting.Weight) && <CircularProgress />}
        {hasSetting(hasSaveErrors, Setting.Weight) && (
          <Alert severity="error">{t('settingsview.weightupdateerror')}</Alert>
        )}
        {hasSetting(hasSaveSuccesses, Setting.Weight) && (
          <Alert severity="success">{t('settingsview.weightupdatesuccess')}</Alert>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={3}>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t('settingsview.temperaturedisplay')}</FormLabel>
          <RadioGroup
            aria-label={t('settingsview.temperaturedisplay')}
            defaultValue={TempType.Celsius}
            name="temperature-display-buttons-group"
            value={isFahrenheit ? TempType.Fahrenheit : TempType.Celsius}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              setIsFahrenheit(event.target.value === TempType.Fahrenheit)
              saveTempDisplaySetting(event.target.value === TempType.Fahrenheit)
            }}
          >
            <FormControlLabel value={TempType.Celsius} control={<Radio />} label={t('settingsview.celsius')} />
            <FormControlLabel value={TempType.Fahrenheit} control={<Radio />} label={t('settingsview.fahrenheit')} />
          </RadioGroup>
        </FormControl>
        {hasSetting(isSavingSettings, Setting.Temperature) && <CircularProgress />}
        {hasSetting(hasSaveErrors, Setting.Temperature) && (
          <Alert severity="error">{t('settingsview.tempupdateerror')}</Alert>
        )}
        {hasSetting(hasSaveSuccesses, Setting.Temperature) && (
          <Alert severity="success">{t('settingsview.tempupdatesuccess')}</Alert>
        )}
      </Stack>
      {!hasSetting(isSavingSettings, Setting.Temperature) ? (
        <Stack direction="row" alignItems="flex-start" spacing={3}>
          <Stack width={200}>
            <NumberField
              label={t('settingsview.s100maxcharttemp')}
              numValue={s100maxTemp}
              valueSuffix={getValueSuffix(SeriesType.TEMPERATURE)}
              minNumValue={convertToTempScale(s100minYAxisTemp, 0)}
              maxNumValue={convertToTempScale(s100maxYAxisTemp, 0)}
              onBlur={(newValue: number) => {
                sets100maxTemp(newValue)

                const hasChanged =
                  Math.round(convertFromTempScale(newValue)) !==
                  Math.round(props.user.s100_chart_max_temp || s100YAxisDefaultTemp)
                if (hasChanged) {
                  saveChartMaxTemp(newValue)
                }
              }}
            />
            <FormHelperText>
              {t('settingsview.charttemphelp').replace(
                '{0}',
                `${convertToTempScale(s100minYAxisTemp, 0)}-${convertToTempScale(s100maxYAxisTemp, 0)}${getValueSuffix(
                  SeriesType.TEMPERATURE
                )}`
              )}
            </FormHelperText>
          </Stack>
          {hasSetting(isSavingSettings, Setting.S100ChartTemp) && <CircularProgress />}
          {hasSetting(hasSaveErrors, Setting.S100ChartTemp) && (
            <Alert severity="error">{t('settingsview.charttempupdateerror')}</Alert>
          )}
          {hasSetting(hasSaveSuccesses, Setting.S100ChartTemp) && (
            <Alert severity="success">{t('settingsview.charttempupdatesuccess')}</Alert>
          )}
        </Stack>
      ) : (
        <CircularProgress />
      )}
      <Stack direction="row" alignItems="flex-start" spacing={3}>
        <Stack width={200}>
          <FormControl>
            <InputLabel htmlFor="ror-interval-select">{t('settingsview.rorinterval')}</InputLabel>
            <Select
              native
              id="ror-interval-select"
              value={`${rorInterval}`}
              label={t('settingsview.rorinterval')}
              onChange={(event: SelectChangeEvent): void => {
                const newrorInterval = parseInt(event.target.value)
                saverorInterval(newrorInterval)
                setrorInterval(newrorInterval)
              }}
            >
              <option value="1">{`1 ${t('settingsview.secondsabbr')}`}</option>
              <option value="5">{`5 ${t('settingsview.secondsabbr')}`}</option>
              <option value="10">{`10 ${t('settingsview.secondsabbr')}`}</option>
              <option value="15">{`15 ${t('settingsview.secondsabbr')}`}</option>
              <option value="20">{`20 ${t('settingsview.secondsabbr')}`}</option>
              <option value="30">{`30 ${t('settingsview.secondsabbr')} (${t('settingsview.default')})`}</option>
              <option value="60">{`60 ${t('settingsview.secondsabbr')}`}</option>
            </Select>
          </FormControl>
          <FormHelperText>{t('settingsview.rorintervalhelp')}</FormHelperText>
        </Stack>
        {hasSetting(isSavingSettings, Setting.RorInterval) && <CircularProgress />}
        {hasSetting(hasSaveErrors, Setting.RorInterval) && (
          <Alert severity="error">{t('settingsview.rorintervalupdateerror')}</Alert>
        )}
        {hasSetting(hasSaveSuccesses, Setting.RorInterval) && (
          <Alert severity="success">{t('settingsview.rorintervalupdatesuccess')}</Alert>
        )}
      </Stack>
      <Stack direction="row" alignItems="flex-end" spacing={3}>
        <Stack width={300}>
          <FormControl component="fieldset">
            <FormLabel sx={{ pb: 2 }} component="legend">
              {t('settingsview.contactinformation')}
              {!email && (
                <Tooltip title={t('navmenu.noemailset')}>
                  <WarningIcon color="warning" sx={{ verticalAlign: 'bottom', ml: 1 }} />
                </Tooltip>
              )}
            </FormLabel>
            <TextField
              label={t('settingsview.email')}
              value={email}
              inputProps={{
                autoCapitalize: 'off',
                autoCorrect: 'off',
              }}
              onChange={(event): void => {
                setEmail(event.target.value)
                if (hasEmailError) {
                  setHasEmailError(!isValidEmail(event.target.value))
                }
              }}
              onBlur={(event): void => {
                const trimmedValue = event.target.value.trim()
                const isValid = isValidEmail(trimmedValue)
                if (isValid && trimmedValue !== props.user.email) {
                  saveEmail(trimmedValue)
                }
                setHasEmailError(!isValid)
                setHasSaveSuccesses((oldSettings) => removeSetting(oldSettings, Setting.Email))
              }}
              helperText={hasEmailError ? t('settingsview.invalidemail') : ''}
              error={hasEmailError}
            />
          </FormControl>
        </Stack>
        {hasSetting(isSavingSettings, Setting.Email) && <CircularProgress />}
        {hasSetting(hasSaveErrors, Setting.Email) && (
          <Alert severity="error">{t('settingsview.emailupdateerror')}</Alert>
        )}
        {hasSetting(hasSaveSuccesses, Setting.Email) && (
          <Alert severity="success">{t('settingsview.emailupdatesuccess')}</Alert>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={3}>
        <FormControlLabel
          control={
            <Switch
              disabled={forcedDarkMode}
              checked={isDarkmode}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                setIsDarkmode(event.target.checked)
                saveDarkmodeSetting(event.target.checked)
              }}
            />
          }
          label={t('settingsview.usedarkmode')}
        />
        {forcedDarkMode && <FormHelperText>{t('settingsview.forceddarkmode')}</FormHelperText>}
        {hasSetting(isSavingSettings, Setting.Darkmode) && <CircularProgress />}
        {hasSetting(hasSaveErrors, Setting.Darkmode) && (
          <Alert severity="error">{t('settingsview.darkmodeupdateerror')}</Alert>
        )}
      </Stack>
      <Button onClick={props.onChangePassword}>{t('settingsview.changepassword')}</Button>
    </Stack>
  )
}
export default UserSettings
